import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  MessagingChannelReplyMode,
  MessagingChannelType,
} from '@treadinc/horizon-api-spec';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback } from 'react';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import MessagingMuteToggle from '~components/Messaging/MessagingMuteToggle';
import useMessagingOpenConversation from '~hooks/useMessaging/useMessagingOpenConversation';
import useMessagingTyping from '~hooks/useMessaging/useMessagingTyping';
import useOpenTargetChannel from '~hooks/useMessaging/useOpenTargetChannel';
import useTranscriptReadState from '~hooks/useMessaging/useTranscriptReadState';
import MessageComposer from '~pages/Messages/components/MessageComposer';
import MessagingAttachmentDropZone from '~pages/Messages/components/MessagingAttachmentDropZone';
import MessagingReconnectingBanner from '~pages/Messages/components/MessagingReconnectingBanner';
import MessagingTranscript from '~pages/Messages/components/MessagingTranscript';
import { canPostToChannel } from '~pages/Messages/messagingComposerPolicy';
import { useMessagingOutbox } from '~pages/Messages/MessagingOutboxContext';
import { MessagingTargetChannelType } from '~queries/messagingQueryKeys';
import useMessagingMessagesInfiniteQuery from '~queries/useMessagingMessagesInfiniteQuery';
import useMessagingTargetChannelQuery from '~queries/useMessagingTargetChannelQuery';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import FirstMessageComposer, { MessageStarter } from './FirstMessageComposer';
import TargetMessagingComposerNote from './TargetMessagingComposerNote';
import TargetMessagingHeader from './TargetMessagingHeader';

// The tab panel sizes to its content, so the transcript needs a bounded box to scroll in;
// 480px shows several messages above the composer without pushing the row off screen.
export const TARGET_MESSAGING_PANEL_HEIGHT_IN_PX = 480;

type TargetMessagingPanelProps = {
  canCreateChannel: boolean;
  channelName: string;
  channelType: MessagingTargetChannelType;
  composerNote?: string;
  firstMessageHint: string;
  headerAvatar: ReactNode;
  headerSubtitle: string;
  headerTitle: string;
  inlineNoticeFor?: (error: unknown) => Nullable<string>;
  isPreparingFirstMessage?: boolean;
  noMessagesMessage: string;
  noPermissionMessage: string;
  readOnly?: boolean;
  startBlockedMessage?: string;
  starters?: MessageStarter[];
  targetId: string;
};

/** Reads the outbox from a `MessagingProviders` above the tab row, so a tab switch keeps a pending send. */
const TargetMessagingPanel = observer(
  ({
    canCreateChannel,
    channelName,
    channelType,
    composerNote,
    firstMessageHint,
    headerAvatar,
    headerSubtitle,
    headerTitle,
    inlineNoticeFor,
    isPreparingFirstMessage = false,
    noMessagesMessage,
    noPermissionMessage,
    readOnly = false,
    startBlockedMessage,
    starters,
    targetId,
  }: TargetMessagingPanelProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const { canModerateMessagingMessage } = userStore.getPermissions();
    const canWriteMessages = !userStore.isViewOnlyUser && !readOnly;
    const viewerUserId = userStore.user?.id;

    const {
      channel,
      isLoading: isLoadingChannel,
      isRefreshing: isRefreshingChannel,
    } = useMessagingTargetChannelQuery({
      channelType,
      targetId,
    });
    const { openTargetChannel } = useOpenTargetChannel();

    const transcript = useMessagingMessagesInfiniteQuery({
      channelId: channel?.id ?? null,
      companyId,
    });
    const {
      fetchOlder,
      hasLoadFailed,
      hasOlder,
      hasOlderLoadFailed,
      isLoading: isLoadingMessages,
      isLoadingOlder,
      messages,
      retryLoad,
    } = transcript;

    const { unreadDividerMessageId } = useTranscriptReadState({
      channel,
      channelId: channel?.id ?? null,
      isRefreshingChannel,
      transcript,
      viewerUserId,
    });
    useMessagingOpenConversation(channel?.id ?? null);

    const { noteTyping, typers } = useMessagingTyping({
      channel,
      isLoadingMessages,
      messages,
      viewerUserId,
    });

    const { entriesByChannelId, retrySend } = useMessagingOutbox();
    const outboxEntries = channel ? (entriesByChannelId[channel.id] ?? []) : [];

    const onRetrySend = useCallback(
      async (clientMessageId: string) => {
        if (!channel) return;

        await retrySend(channel, clientMessageId);
      },
      [channel, retrySend],
    );

    const openChannel = useCallback(
      () => openTargetChannel({ channelType, name: channelName, targetId }),
      [channelName, channelType, openTargetChannel, targetId],
    );

    const hasHistory = messages.length > 0 || outboxEntries.length > 0;
    const visibleComposerNote = canPostToChannel(channel, viewerUserId)
      ? composerNote
      : undefined;
    // One key per target whether or not its channel exists yet, so a draft survives the channel turning up.
    const draftKey = `${channelType}:${targetId}`;
    const showsTranscript =
      Boolean(channel) && (hasHistory || isLoadingMessages || hasLoadFailed);
    const canSendFirstMessage =
      canWriteMessages &&
      !startBlockedMessage &&
      (channel ? canPostToChannel(channel, viewerUserId) : canCreateChannel);

    const emptyStateMessage = () => {
      if (!canWriteMessages) return noMessagesMessage;
      if (startBlockedMessage) return startBlockedMessage;

      return noPermissionMessage;
    };

    const renderBody = () => {
      const isWaitingToOfferFirstMessage =
        canSendFirstMessage && isPreparingFirstMessage && !showsTranscript;

      if (isLoadingChannel || isWaitingToOfferFirstMessage) {
        return (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        );
      }

      if (channel && showsTranscript) {
        return (
          <MessagingAttachmentDropZone>
            <MessagingTranscript
              canModerate={Boolean(canModerateMessagingMessage)}
              channel={channel}
              hasLoadFailed={hasLoadFailed}
              hasOlder={hasOlder}
              hasOlderLoadFailed={hasOlderLoadFailed}
              isLoading={isLoadingMessages}
              isLoadingOlder={isLoadingOlder}
              key={channel.id}
              messages={messages}
              onLoadOlder={fetchOlder}
              onRetryLoad={retryLoad}
              onRetrySend={onRetrySend}
              outboxEntries={outboxEntries}
              typers={typers}
              unreadDividerMessageId={unreadDividerMessageId}
              viewerUserId={viewerUserId}
            />

            {canWriteMessages && (
              <>
                {visibleComposerNote && (
                  <Box
                    sx={{
                      backgroundColor: theme.semantic.surface,
                      borderTop: `1px solid ${theme.semantic.divider}`,
                      px: 2,
                      pt: 1,
                    }}
                  >
                    <TargetMessagingComposerNote note={visibleComposerNote} />
                  </Box>
                )}

                <MessageComposer
                  channel={channel}
                  draftKey={draftKey}
                  key={`composer-${channel.id}`}
                  onTyping={noteTyping}
                />
              </>
            )}
          </MessagingAttachmentDropZone>
        );
      }

      if (canSendFirstMessage) {
        return (
          <FirstMessageComposer
            composerNote={composerNote}
            draftKey={draftKey}
            existingChannel={channel}
            key={`first-message-${draftKey}`}
            hint={firstMessageHint}
            inlineNoticeFor={inlineNoticeFor}
            onTyping={noteTyping}
            openChannel={openChannel}
            starters={starters}
            typers={typers}
          />
        );
      }

      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            p: 3,
          }}
        >
          <EmptyStateMessage message={emptyStateMessage()} />
        </Box>
      );
    };

    // A job channel only ever holds the dispatcher and the driver, so its reply mode says nothing.
    const showsReplyMode = channelType === MessagingChannelType.ORDER;

    return (
      <Box
        data-test-id={`${channelType}-messaging-tab`}
        sx={{
          border: `1px solid ${theme.semantic.divider}`,
          borderRadius: theme.brandV2.borderRadius,
          display: 'flex',
          flexDirection: 'column',
          height: `${TARGET_MESSAGING_PANEL_HEIGHT_IN_PX}px`,
          overflow: 'hidden',
        }}
      >
        <TargetMessagingHeader
          avatar={headerAvatar}
          channel={channel}
          endAction={
            channel && <MessagingMuteToggle channel={channel} key={channel.id} />
          }
          replyMode={
            showsReplyMode
              ? (channel?.replyMode ?? MessagingChannelReplyMode.FORUM)
              : undefined
          }
          subtitle={headerSubtitle}
          title={headerTitle}
        />

        <MessagingReconnectingBanner />

        {renderBody()}
      </Box>
    );
  },
);

export default TargetMessagingPanel;
